import React, { useMemo } from 'react';

/**
 * Capital3DBars — Visualisation immersive du capital levé mois par mois
 * (variante "AR/VR-inspired" du graphique recharts standard).
 *
 * - Rendu CSS 3D pur (aucune lib), respect prefers-reduced-motion.
 * - `data` : [{ month: '2026-01', total: 1500 }, ...]
 * - Hauteur relative en fonction du max de la série.
 * - Chaque barre affiche une face latérale + supérieure via ::after/::before + spans.
 */
export function Capital3DBars({ data = [], height = 200, testId = 'capital-3d-bars' }) {
  const items = useMemo(() => (data || []).slice(-9), [data]);
  const max = useMemo(() => Math.max(1, ...items.map((r) => Number(r.total) || 0)), [items]);

  if (items.length === 0) {
    return (
      <div className="text-xs text-[#5E6D66] text-center py-8" data-testid={`${testId}-empty`}>
        Aucune donnée mensuelle
      </div>
    );
  }

  return (
    <div
      className="flex items-end justify-around gap-2 px-4 pt-2 pb-6 select-none"
      style={{ height, perspective: '900px' }}
      data-testid={testId}
    >
      {items.map((row, idx) => {
        const ratio = (Number(row.total) || 0) / max;
        const barH = Math.max(6, Math.round(ratio * (height - 70)));
        const monthLabel = String(row.month || '').slice(5);
        return (
          <div key={row.month || idx} className="flex-1 min-w-0 max-w-20 flex flex-col items-center gap-1 group">
            <div
              className="text-[10px] font-semibold text-[#2D5A4C] text-center [overflow-wrap:anywhere] max-w-full"
              data-testid={`${testId}-value-${idx}`}
            >
              {new Intl.NumberFormat('fr-FR', { notation: 'compact', maximumFractionDigits: 1 }).format(Number(row.total) || 0)} €
            </div>
            <div
              className="bar3d w-full max-w-12 rounded-t"
              style={{
                height: `${barH}px`,
                background: 'linear-gradient(180deg, #3A7565 0%, #2D5A4C 100%)',
              }}
              title={`${row.month} — ${Math.round(Number(row.total) || 0)} €`}
            >
              <span className="bar3d-side" style={{ background: '#2D5A4C' }} />
              <span className="bar3d-top"  style={{ background: '#3A7565' }} />
            </div>
            <div className="text-[10px] text-[#5E6D66]" data-testid={`${testId}-month-${idx}`}>{monthLabel}</div>
          </div>
        );
      })}
    </div>
  );
}
